import { useId, forwardRef, useRef, useImperativeHandle } from 'react';
import styles from './common.module.scss';
import { Link } from 'react-router';
import { UIText } from '../ui-text';
import CreatableSelect from 'react-select/creatable';
import { blue, moonlight, red, redLight } from '../../styles';
import { UITextButton } from '../..';

export type UICreatableMultiSelectInputProps = {
    name?: string;
    label?: string;
    labelButton?: {
        text: string;
        link: string;
    };
    value?: { value: string; label: string }[];
    options?: { value: string; label: string }[];
    placeholder?: string;
    description?: string;
    required?: boolean;
    disabled?: boolean;
    errorText?: string;
    onChange?: (options: { value: string; label: string }[]) => void;
};

export type UICreatableMultiSelectInputRef = {
    focus: () => void;
};

export const UICreatableMultiSelectInput = forwardRef<
    UICreatableMultiSelectInputRef,
    UICreatableMultiSelectInputProps
>((props, ref) => {
    const {
        name,
        label,
        labelButton,
        value,
        options,
        placeholder,
        description = '',
        required = false,
        //disabled = false,
        errorText = '',
        onChange
    } = props;

    const id = useId();

    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => ({
        focus() {
            inputRef.current?.focus();
        }
    }));

    return (
        <div className={styles.container}>
            {label && (
                <div className={styles.labelContainer}>
                    <div>
                        <label className={styles.label} htmlFor={id}>
                            {label}
                        </label>
                        {required ? (
                            <label
                                className={`${styles.label} ${styles.labelRequired}`}
                                htmlFor={id}
                            >
                                {' *'}
                            </label>
                        ) : undefined}
                    </div>

                    {labelButton && (
                        <Link
                            to={labelButton.link}
                            style={{ textDecoration: 'none' }}
                        >
                            <UITextButton variant="primary">
                                {labelButton.text}
                            </UITextButton>
                        </Link>
                    )}
                </div>
            )}
            <CreatableSelect
                name={name}
                value={value}
                options={options}
                isMulti
                closeMenuOnSelect={false}
                placeholder={placeholder}
                onChange={(_selectedOptions) => {
                    const selectedOptions = _selectedOptions.map((option) => {
                        return { value: option.value, label: option.label };
                    });
                    onChange?.(selectedOptions);
                }}
                classNames={{
                    control: () => styles.reactSelectControl,
                    singleValue: () => styles.reactSelectSingleValue,
                    option: () => styles.reactSelectOption,
                    placeholder: () => styles.reactSelectPlaceholder,
                    menu: () => styles.reactSelectMenu
                }}
                theme={(theme) => ({
                    ...theme,
                    borderRadius: 5,
                    borderWidth: 1,
                    colors: {
                        ...theme.colors,
                        primary25: moonlight,
                        primary: blue,
                        danger: red,
                        dangerLight: redLight,
                        neutral10: moonlight
                    }
                })}
            />
            {errorText && (
                <div className={styles.errorTextContainer}>
                    <UIText variant="small" type="error">
                        {errorText}
                    </UIText>
                </div>
            )}
            {!errorText && description && (
                <div className={styles.descriptionTextContainer}>
                    <UIText variant="small" type="default2">
                        {description}
                    </UIText>
                </div>
            )}
        </div>
    );
});
